import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, Mail, Loader2, Sparkles } from "lucide-react";
import { buildRfpHtml, printRfp } from "@/lib/rfp";
import { useRfpEmail, carrierEmails } from "@/hooks/useRfpEmail";
import { agentCcLabel } from "@/hooks/useQuoteEmail";
import { useRfpAi } from "@/hooks/useRfpAi";
import { CarrierPicker, CarrierChips } from "@/components/CarrierPicker";
import { toast } from "sonner";

export default function RFPModal({ open, onOpenChange, rfpData, carriers = [] }) {
  const emailCarriers = carriers.filter((c) => !c.disabled && carrierEmails(c).length > 0);
  const {
    selectedIds,
    selectedCarriers,
    extraEmails,
    setExtraEmails,
    ccOwner,
    setCcOwner,
    ccAgent,
    setCcAgent,
    attachPdf,
    setAttachPdf,
    sending,
    agentEmails,
    recipients,
    toggleCarrier,
    addSelections,
    selectAllPreferred,
    selectAll,
    clearSelection,
    sendEmail,
  } = useRfpEmail(rfpData, emailCarriers);

  const { aiNotes, drafting, recommendedIds, rationale, recommending, aiRecommend, draftNotes } =
    useRfpAi(rfpData, emailCarriers, addSelections);

  const ccLabel = useMemo(() => agentCcLabel(agentEmails), [agentEmails]);

  const html = buildRfpHtml({ ...rfpData, aiNotes });

  const handlePrint = () => {
    const ok = printRfp(html);
    if (!ok) toast.error("Please allow pop-ups to print / save the RFP as PDF");
  };

  const handleEmail = () => sendEmail(html);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-5 border-b border-border">
          <DialogTitle className="text-[#1C1917]">Request for Shipping Quote (RFP)</DialogTitle>
          <DialogDescription>Print this branded RFP, or email it to one or more carriers — a PDF copy is attached to the email automatically.</DialogDescription>
        </DialogHeader>

        <div className="px-5 py-3 bg-stone-50 border-b border-border space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={handlePrint} className="bg-[#1C1917] hover:bg-black text-white" data-testid="rfp-print-button">
              <Printer className="h-4 w-4 mr-2" /> Download / Print PDF
            </Button>
            <Button
              onClick={draftNotes}
              disabled={drafting}
              variant="outline"
              className="border-[#F97316]/40 text-[#EA580C] hover:bg-[#FFF7ED]"
              data-testid="rfp-ai-draft-button"
            >
              {drafting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              AI Draft Notes
            </Button>
            <Button
              onClick={aiRecommend}
              disabled={recommending}
              variant="outline"
              className="border-[#F97316]/40 text-[#EA580C] hover:bg-[#FFF7ED]"
              data-testid="rfp-ai-recommend-button"
            >
              {recommending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              AI Recommend Carriers
            </Button>
          </div>
          {rationale && (
            <p className="text-xs text-[#57534E] italic leading-relaxed" data-testid="rfp-ai-rationale">
              {rationale}
            </p>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px]">
              <Label className="text-[11px] font-bold uppercase text-[#57534E]">Send to carriers</Label>
              <CarrierPicker
                emailCarriers={emailCarriers}
                selectedIds={selectedIds}
                onToggle={toggleCarrier}
                selectedCount={selectedCarriers.length}
                recommendedIds={recommendedIds}
                onSelectPreferred={selectAllPreferred}
                onSelectAll={selectAll}
                onClear={clearSelection}
              />
            </div>

            <div className="flex-1 min-w-[220px]">
              <Label className="text-[11px] font-bold uppercase text-[#57534E]">Other emails (comma-separated)</Label>
              <Input
                type="text"
                placeholder="carrier@example.com, dispatch@example.com"
                value={extraEmails}
                onChange={(e) => setExtraEmails(e.target.value)}
                className="mt-1 focus-visible:ring-2 focus-visible:ring-[#F97316]/30"
                data-testid="rfp-email-input"
              />
            </div>

            <Button
              onClick={handleEmail}
              disabled={sending}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white shrink-0"
              data-testid="rfp-send-email-button"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              <span className="ml-1.5">Send{recipients.length ? ` (${recipients.length})` : ""}</span>
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="rfp-attach-pdf">
              <Checkbox checked={attachPdf} onCheckedChange={(v) => setAttachPdf(!!v)} />
              <span className="text-xs text-[#1C1917]">Include PDF attachment</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="rfp-cc-owner">
              <Checkbox checked={ccOwner} onCheckedChange={(v) => setCcOwner(!!v)} />
              <span className="text-xs text-[#1C1917]">Send a copy to the owner (ike@kgmowers.com)</span>
            </label>
            {ccLabel.count > 0 && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid="rfp-cc-agent">
                <Checkbox checked={ccAgent} onCheckedChange={(v) => setCcAgent(!!v)} />
                <span className="text-xs text-[#1C1917]">
                  Copy sales agent{ccLabel.count > 1 ? "s" : ""} ({ccLabel.text})
                </span>
              </label>
            )}
            <CarrierChips selectedCarriers={selectedCarriers} onToggle={toggleCarrier} />
          </div>
        </div>

        <div className="overflow-auto p-4 bg-stone-200" data-testid="rfp-preview">
          <iframe
            title="RFP Preview"
            srcDoc={html}
            className="w-full bg-white rounded-md border border-border"
            style={{ height: "50vh" }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
